import { Buffer } from "buffer";
import Logger from "../logger";
import base from "./base";
import axios from "axios";
import { baseContainer } from "../index";
import { changeStatus } from "../hooks/useLogin";

export default class AuthenticationService {
  constructor() {
    /** @type {String} Bearer token for back-end API scope */
    this.token = null;
    /** @type {String} Back-end JWT auth token */
    this.authToken = null;
    /** @type {Object} Deserialized back-end auth token */
    this.decodedAuthToken = null;
    /** @type {String} Selected tenant id  */
    this.tenantId = null;
    /** @type {Array} User's tenants */
    this.userTenants = null;
    /** @type {Array} User claim permissions */
    this.permissions = [];
    /** @type {Number} Holds the interval function for health check initialization */
    this.healthCheck = null;
    /**@type {String} Local storage key string for back-end token */
    this.apiAuthTokenKey = "apiAuthTokenKey";
    /**@type {String} Local storage key string for flagging a pending back-end token request */
    this.requestingTokenKey = "requestingTokenKey";
    /**@type {String} Local storage key string for selected tenant */
    this.tenantIdKey = "tenantId";
    this.eywpTenantIdKey = "selectedTenant";
    this.isEywpMfe = process.env.REACT_APP_EYWP_MFE === "true";
  }

  /**
   * Log in functionality
   */
  login() { }

  /**
   * Log out functionality
   */
  logout() { }

  /**
   * Method that executes after log out process
   */
  postLogout() { }

  /**
   * Select tenant functionality
   */
  async selectTenant(tenant) { }

  /**
   * Request a back-end API auth token
   */
  async getAuthToken() { }

  /**
   * One-off Login with selected tenant.Triggers after initial login when user selects tenant.
   * * @param {String} tenantId Value of the selected tenantId that will be saved in local storage
   */
  async extendLogInWithTenant(tenantId) {
    this.setRequestingToken(true);
    try {
      this.authToken = await this.extendToken(tenantId);
      this.setRequestingToken(false);
    } catch (err) {
      Logger.of("App.AuthenticationService.extendLogInWithTenant").error(err);
      this.removeTenant();
      this.setRequestingToken(false);
      throw err;
    }
  }

  /**
   * Request a back-end API auth token specific to an tenant
   * @returns @type {String}
   * @param {String} tenantId Value of the selected tenantId that will be saved in local storage
   */
  async extendToken(tenantId) {
    let config = baseContainer.getConfig();
    try {
      this.setSelectedTenant(tenantId);
      let response = await axios.request({
        baseURL: config.apiEndpoints.base,
        url: config.apiEndpoints.identityTenantToken,
        params: { 
          tenantId,
          moduleName: config.appName,
        },
        headers: {
          Authorization: `Bearer ${this.token}`,
          access_token: this.getTokenFromStorage(),
        },
      });

      this.saveTokenToStorage(response.data.authToken);
      this.authToken = response.data.authToken;

      // initialize user claims
      this.setPermissions();
    } catch (err) {
      Logger.of("App.AuthenticationService.extendToken").error(err);
      this.removeTenant();
      throw err;
    }
    return this.authToken;
  }

  /**
   * Function wrapper that sets useLogin Hook State,the login status should be managed only through here
   */
  changeStatus(status) {
    changeStatus(status);
  }

  /**
   * Get back-end auth token value from local storage
   * @returns @type {String}
   */
  getTokenFromStorage() {
    if (sessionStorage.getItem(this.apiAuthTokenKey) === "undefined") return null;
    return sessionStorage.getItem(this.apiAuthTokenKey);
  }

  /**
   * Save the back-end auth token to local storage
   * @param {String} token Value of the back-end auth token that will be saved in local storage
   */
  saveTokenToStorage(token) {
    sessionStorage.setItem(this.apiAuthTokenKey, token);
  }

  /**
   * Remove auth token from local storage
   * @returns @type {String}
   */
  removeTokenFromStorage() {
    sessionStorage.removeItem(this.apiAuthTokenKey);
  }

  /**
   * Get flag indicating whether a back-end auth token request is pending, from local storage
   * @returns @type {Boolean}
   */
  isRequestingToken() {
    let requestingToken = sessionStorage.getItem(this.requestingTokenKey);
    return requestingToken !== null && requestingToken === "true";
  }

  /**
   * Save flag indicating whether a back-end auth token request is pending, to local storage
   * @param {Boolean} requestingToken
   */
  setRequestingToken(requestingToken) {
    sessionStorage.setItem(this.requestingTokenKey, requestingToken);
  }

  /**
   * Method to check if a specific permission is listed in the user's claims
   * @param {String} permission
   * @returns {Boolean}
   */
  hasPermission(permission) {
    return this.permissions.indexOf(permission) !== -1;
  }

  /**
   * Check if authentication token has expired
   * @returns {Boolean}
   */
  authTokenExpired() {
    if (!this.authToken) this.authToken = this.getTokenFromStorage();
    if (!this.authToken) return true;
    return (
      JSON.parse(Buffer.from(this.authToken.split(".")[1], "base64").toString())
        .exp -
      60 * 1 <
      new Date().getTime() / 1000
    );
  }

  /**
   * Extract user claims (permissions) from the back-end JWT authorization token
   */
  setPermissions() {
    let authToken = this.getTokenFromStorage();
    let config = baseContainer.getConfig();
    if (authToken) {
      this.decodedAuthToken = JSON.parse(
        Buffer.from(authToken.split(".")[1], "base64").toString()
      );

      this.permissions = [];

      let commonPermissions =
        this.decodedAuthToken[config.claimTypes.commonPermissions];

      if (commonPermissions) {
        if (Array.isArray(commonPermissions)) {
          commonPermissions.forEach((x) => this.permissions.push(x));
        } else {
          this.permissions.push(commonPermissions);
        }
      }

      let adminPermissions =
        this.decodedAuthToken[config.claimTypes.adminPermissions];

      if (adminPermissions) {
        if (Array.isArray(adminPermissions)) {
          adminPermissions.forEach((x) => this.permissions.push(x));
        } else {
          this.permissions.push(adminPermissions);
        }
      }

      let tenantPermissions =
        this.decodedAuthToken[config.claimTypes.tenantPermissions];

      if (tenantPermissions) {
        if (Array.isArray(tenantPermissions)) {
          tenantPermissions.forEach((x) => this.permissions.push(x));
        } else {
          this.permissions.push(tenantPermissions);
        }
      }
    }
  }

  /**
   * Gets user tenants.
   * @returns {Array}
   */
  async getTenants(shouldRefresh = false) {
    let config = baseContainer.getConfig();
    Logger.of("App.AuthenticationService.getTenants").info({
      tenants: this.userTenants,
    });
    if (shouldRefresh || !this.userTenants) {
      try {
        let response = await base.get({
          url: config.apiEndpoints.identityTenants,
          params: {
            eywpTenantId: this.getEYWPTenantId()
          }
        });
        Logger.of("App.AuthenticationService.getTenants").info(response);
        this.userTenants = response.data;
      } catch (err) {
        Logger.of("App.AuthenticationService.getTenants").error(err);
      }
    }
    return this.userTenants;
  }

  /**
   * Sets selected tenant id in local storage.
   */
  setSelectedTenant(tenantId) {
    sessionStorage.setItem(this.tenantIdKey, tenantId);
  }

  /**
   * Gets selected tenant id from local storage.
   * @returns {String}
   */
  getSelectedTenant() {
    return sessionStorage.getItem(this.tenantIdKey);
  }

  getEYWPTenantId() {
    return sessionStorage.getItem(this.eywpTenantIdKey);
  }

  /**
   * Remove tenant from local storage.
   */
  removeTenant() {
    sessionStorage.removeItem(this.tenantIdKey);
  }

  /**
   * Checking if we force user to have tenant and if so if he has selected one based on tenant claim from token
   * @returns @type {Boolean}
   */
  canViewApp() {
    let config = baseContainer.getConfig();
    Logger.of("App.AuthenticationService.canViewApp").info({
      REACT_APP_FORCE_TENANT: config.forceTenant,
      check: config.forceTenant,
      hasTenant: this.#hasTenant(),
    });
    return !config.forceTenant || this.#hasTenant();
  }

  /**
   * Checking if user has only Learner role
   * @returns @type {Boolean}
   */
  hasRedirectPermissions(permissionsArray) {
    return (
      this.permissions.length > 0 &&
      this.permissions.every((x) => permissionsArray.some((y) => x.includes(y)))
    );
  }

  /**
   * Reading token to see if user has selected a tenant
   * @returns @type {Boolean}
   */
  #hasTenant() {
    let config = baseContainer.getConfig();
    //It is safe to assume that token is already decoded here because user has already gone through acquiring the 1st token
    //The additional check for tenant id in local storage is to prevent user for going to home page when he clicks
    //to switch tenant and refreshes the page without selecting one.
    return (
      Object.prototype.hasOwnProperty.call(
        this.decodedAuthToken,
        config.claimTypes.tenant
      ) && this.getSelectedTenant()
    );
  }
}
